/**
 * The rule tables of TeXcount 3.1.1 (Einar Andreas Rødland, LaTeX Project Public
 * License): which macros take how many arguments, which environments are text, math,
 * floats or ignored, and the per-package additions.
 *
 * The tables are transcribed from `texcount.pl` so that this counter and the Perl
 * original agree on the same document. Keys name a parser state; `[` is an optional
 * argument whose contents follow in the next entry.
 */

/** Parser states, numbered as in TeXcount. Positive states count words. */
export const ST = {
  text: 1,
  headerText: 2,
  floatText: 3,
  toHeader: 4,
  toFloat: 5,
  toInline: 6,
  toDisplay: 7,
  ignore: -1,
  math: -2,
  float: -10,
  xx: -20,
  xxx: -30,
  xall: -40,
  special: -90,
  preamble: -99,
  option: -1000,
  noOption: -1001,
  autoOption: -1002,
} as const;

/** Counter indexes, as in TeXcount's count arrays. */
export const CNT = {
  file: 0,
  text: 1,
  header: 2,
  other: 3,
  headers: 4,
  floats: 5,
  inline: 6,
  display: 7,
} as const;

const STATE_KEYS: Record<string, number> = {
  text: ST.text,
  word: ST.text,
  w: ST.text,
  wd: ST.text,
  headertext: ST.headerText,
  headerword: ST.headerText,
  hword: ST.headerText,
  hw: ST.headerText,
  hwd: ST.headerText,
  otherword: ST.floatText,
  other: ST.floatText,
  oword: ST.floatText,
  ow: ST.floatText,
  owd: ST.floatText,
  header: ST.toHeader,
  heading: ST.toHeader,
  head: ST.toHeader,
  float: ST.toFloat,
  table: ST.toFloat,
  figure: ST.toFloat,
  inline: ST.toInline,
  inlinemath: ST.toInline,
  imath: ST.toInline,
  eq: ST.toInline,
  displaymath: ST.toDisplay,
  dsmath: ST.toDisplay,
  dmath: ST.toDisplay,
  ds: ST.toDisplay,
  ignore: ST.ignore,
  x: ST.ignore,
  ismath: ST.math,
  isfloat: ST.float,
  xx: ST.xx,
  xxx: ST.xxx,
  xall: ST.xall,
  specarg: ST.special,
  specialargument: ST.special,
  "[": ST.option,
  " option": ST.option,
  " opt": ST.option,
  " optional": ST.option,
  nooption: ST.noOption,
  nooptions: ST.noOption,
  noopt: ST.noOption,
  noopts: ST.noOption,
  autooption: ST.autoOption,
  autooptions: ST.autoOption,
  autoopt: ST.autoOption,
  autoopts: ST.autoOption,
};

const COUNT_KEYS: Record<string, number> = {
  text: CNT.text,
  word: CNT.text,
  w: CNT.text,
  wd: CNT.text,
  headerword: CNT.header,
  hword: CNT.header,
  hw: CNT.header,
  hwd: CNT.header,
  otherword: CNT.other,
  other: CNT.other,
  oword: CNT.other,
  ow: CNT.other,
  owd: CNT.other,
  header: CNT.headers,
  heading: CNT.headers,
  head: CNT.headers,
  float: CNT.floats,
  table: CNT.floats,
  figure: CNT.floats,
  inline: CNT.inline,
  inlinemath: CNT.inline,
  imath: CNT.inline,
  eq: CNT.inline,
  displaymath: CNT.display,
  dsmath: CNT.display,
  dmath: CNT.display,
  ds: CNT.display,
};

/** A macro's rule: how many arguments to skip, or one state per argument. */
export type MacroRule = number | number[];

export function stateOf(key: string): number | undefined {
  return STATE_KEYS[key.toLowerCase()];
}

export function countOf(key: string): number | undefined {
  return COUNT_KEYS[key.toLowerCase()];
}

type RawRule = number | string[];

function toRule(raw: RawRule): MacroRule {
  if (typeof raw === "number") {
    return raw;
  }
  return raw.map((key) => stateOf(key) ?? ST.ignore);
}

export interface RuleSet {
  /** Arguments each macro takes, and what to do with them. Environments: `begin<name>`. */
  macro: Map<string, MacroRule>;
  /** Macros that carry counted text in the preamble: \title, \thanks. */
  preamble: Map<string, MacroRule>;
  /** Macros counted inside a float: \caption. */
  floatInc: Map<string, MacroRule>;
  /** Package-loading macros. */
  packageInc: Map<string, MacroRule>;
  /** The state an environment's contents are read in. */
  envir: Map<string, number>;
  /** Macros that stand for words: \LaTeX is one word. A list adds to counters. */
  macroCount: Map<string, number | number[]>;
  /** File inclusion macros and their kind. */
  fileInclude: Map<string, string>;
}

function put(
  map: Map<string, MacroRule>,
  rule: RawRule,
  ...names: string[]
): void {
  for (const name of names) {
    map.set(name, toRule(rule));
  }
}

function putEnvir(
  map: Map<string, number>,
  key: string,
  ...names: string[]
): void {
  for (const name of names) {
    map.set(name, stateOf(key) ?? ST.ignore);
  }
}

export function defaultRules(): RuleSet {
  const preamble = new Map<string, MacroRule>();
  const floatInc = new Map<string, MacroRule>();
  const packageInc = new Map<string, MacroRule>();
  put(preamble, ["header"], "\\title");
  put(preamble, ["other"], "\\thanks");
  put(preamble, ["xxx", "xxx"], "\\newcommand", "\\renewcommand");
  put(
    preamble,
    ["xxx", "xxx", "xxx"],
    "\\newenvironment",
    "\\renewenvironment"
  );
  put(floatInc, ["otherword"], "\\caption");
  put(
    packageInc,
    ["[", "ignore", "specialargument"],
    "\\usepackage",
    "\\RequirePackage"
  );

  const macro = new Map<string, MacroRule>([
    ...preamble,
    ...floatInc,
    ...packageInc,
  ]);
  put(
    macro,
    ["text"],
    "\\textnormal",
    "\\textrm",
    "\\textit",
    "\\textbf",
    "\\textsf",
    "\\texttt",
    "\\textsc",
    "\\textsl",
    "\\textup",
    "\\textmd",
    "\\makebox",
    "\\mbox",
    "\\framebox",
    "\\fbox",
    "\\uppercase",
    "\\lowercase",
    "\\textsuperscript",
    "\\textsubscript",
    "\\citetext"
  );
  put(macro, ["[", "text"], "\\item");
  put(
    macro,
    ["[", "ignore"],
    "\\linebreak",
    "\\nolinebreak",
    "\\pagebreak",
    "\\nopagebreak"
  );
  put(
    macro,
    0,
    "\\maketitle",
    "\\indent",
    "\\noindent",
    "\\centering",
    "\\raggedright",
    "\\raggedleft",
    "\\clearpage",
    "\\cleardoublepage",
    "\\newline",
    "\\newpage",
    "\\smallskip",
    "\\medskip",
    "\\bigskip",
    "\\vfill",
    "\\hfill",
    "\\hrulefill",
    "\\dotfill",
    "\\normalsize",
    "\\small",
    "\\footnotesize",
    "\\scriptsize",
    "\\tiny",
    "\\large",
    "\\Large",
    "\\LARGE",
    "\\huge",
    "\\Huge",
    "\\normalfont",
    "\\em",
    "\\rm",
    "\\it",
    "\\bf",
    "\\sf",
    "\\tt",
    "\\sc",
    "\\sl",
    "\\rmfamily",
    "\\sffamily",
    "\\ttfamily",
    "\\upshape",
    "\\itshape",
    "\\slshape",
    "\\scshape",
    "\\mdseries",
    "\\bfseries",
    "\\selectfont",
    "\\tableofcontents",
    "\\listoftables",
    "\\listoffigures"
  );
  put(
    macro,
    1,
    "\\begin",
    "\\end",
    "\\documentclass",
    "\\documentstyle",
    "\\hyphenation",
    "\\pagestyle",
    "\\thispagestyle",
    "\\author",
    "\\date",
    "\\bibliographystyle",
    "\\bibliography",
    "\\pagenumbering",
    "\\markright",
    "\\includeonly",
    "\\includegraphics",
    "\\special",
    "\\label",
    "\\ref",
    "\\pageref",
    "\\bibitem",
    "\\eqlabel",
    "\\eqref",
    "\\hspace",
    "\\vspace",
    "\\addvspace",
    "\\newsavebox",
    "\\usebox",
    "\\newlength",
    "\\newcounter",
    "\\stepcounter",
    "\\refstepcounter",
    "\\usecounter",
    "\\fontfamily",
    "\\fontseries",
    "\\alph",
    "\\arabic",
    "\\fnsymbol",
    "\\roman",
    "\\value",
    "\\typeout",
    "\\typein",
    "\\cline"
  );
  put(
    macro,
    2,
    "\\newfont",
    "\\newtheorem",
    "\\sbox",
    "\\savebox",
    "\\rule",
    "\\markboth",
    "\\setlength",
    "\\addtolength",
    "\\settodepth",
    "\\settoheight",
    "\\settowidth",
    "\\setcounter",
    "\\addtocontents",
    "\\addtocounter",
    "\\fontsize"
  );
  put(macro, 3, "\\addcontentsline");
  put(macro, 6, "\\DeclareFontShape");
  put(
    macro,
    ["[", "text", "ignore"],
    "\\cite",
    "\\nocite",
    "\\citep",
    "\\citet",
    "\\citeauthor",
    "\\citeyear",
    "\\citeyearpar",
    "\\citealp",
    "\\citealt",
    "\\Citep",
    "\\Citet",
    "\\Citealp",
    "\\Citealt",
    "\\Citeauthor"
  );
  put(macro, ["ignore", "text"], "\\parbox", "\\raisebox");
  put(macro, ["otherword"], "\\marginpar", "\\footnote", "\\footnotetext");
  put(
    macro,
    ["header"],
    "\\title",
    "\\part",
    "\\chapter",
    "\\section",
    "\\subsection",
    "\\subsubsection",
    "\\paragraph",
    "\\subparagraph"
  );
  put(macro, ["xxx", "xxx", "text"], "\\multicolumn");
  put(macro, ["xxx", "xxx"], "\\newcommand", "\\renewcommand");
  put(macro, ["xxx", "xxx", "xxx"], "\\newenvironment", "\\renewenvironment");

  const envir = new Map<string, number>();
  putEnvir(
    envir,
    "ignore",
    "titlepage",
    "tabbing",
    "tabular",
    "tabular*",
    "thebibliography",
    "lrbox"
  );
  putEnvir(
    envir,
    "text",
    "document",
    "letter",
    "center",
    "flushleft",
    "flushright",
    "abstract",
    "quote",
    "quotation",
    "verse",
    "minipage",
    "description",
    "enumerate",
    "itemize",
    "list",
    "theorem",
    "thm",
    "lemma",
    "definition",
    "corollary",
    "example",
    "proof",
    "pf"
  );
  putEnvir(envir, "inlinemath", "math");
  putEnvir(
    envir,
    "displaymath",
    "displaymath",
    "equation",
    "equation*",
    "eqnarray",
    "eqnarray*",
    "align",
    "align*"
  );
  putEnvir(
    envir,
    "float",
    "float",
    "picture",
    "figure",
    "figure*",
    "table",
    "table*"
  );
  putEnvir(envir, "xall", "verbatim", "tikzpicture");

  put(macro, 1, "beginthebibliography", "beginlrbox", "beginminipage");
  put(macro, 2, "beginlist");
  put(macro, ["ignore"], "beginletter");
  put(macro, ["xxx"], "begintabular");
  put(macro, ["ignore", "xxx"], "begintabular*");
  put(
    macro,
    ["[", "text"],
    "begintheorem",
    "beginthm",
    "beginlemma",
    "begindefinition",
    "begincorollary",
    "beginexample",
    "beginproof",
    "beginpf"
  );
  put(macro, ["nooptions"], "beginverbatim");

  const macroCount = new Map<string, number | number[]>([
    ["\\LaTeX", 1],
    ["\\TeX", 1],
    ["beginabstract", [CNT.headers, CNT.header]],
  ]);
  const fileInclude = new Map<string, string>([
    ["\\input", "input"],
    ["\\include", "texfile"],
  ]);
  return {
    macro,
    preamble,
    floatInc,
    packageInc,
    envir,
    macroCount,
    fileInclude,
  };
}

interface PackageRules {
  macro?: Record<string, RawRule>;
  envir?: Record<string, string>;
  macroCount?: Record<string, number>;
}

const COLOR_MACROS: Record<string, RawRule> = {
  "\\textcolor": ["ignore", "text"],
  "\\color": 1,
  "\\pagecolor": 1,
  "\\normalcolor": 0,
  "\\colorbox": ["ignore", "text"],
  "\\fcolorbox": ["ignore", "ignore", "text"],
  "\\definecolor": 3,
};

/** Package-specific rules, applied when `\usepackage{name}` is read. */
export const PACKAGE_RULES: Record<string, PackageRules> = {
  alltt: { envir: { alltt: "xall" } },
  babel: {
    envir: { otherlanguage: "text", "otherlanguage*": "text" },
    macro: {
      "\\selectlanguage": 1,
      "\\foreignlanguage": ["ignore", "text"],
      beginotherlanguage: 1,
      "beginotherlanguage*": 1,
    },
  },
  comment: { envir: { comment: "xxx" } },
  color: { macro: COLOR_MACROS },
  xcolor: { macro: { ...COLOR_MACROS, "\\colorlet": 2 } },
  endnotes: {
    macro: {
      "\\endnote": ["oword"],
      "\\endnotetext": ["oword"],
      "\\addtoendnotetext": ["oword"],
    },
  },
  etoolbox: {
    macro: {
      "\\apptocmd": ["xxx", "ignore", "ignore", "ignore"],
      "\\pretocmd": ["xxx", "ignore", "ignore", "ignore"],
      "\\patchcmd": ["xxx", "xxx", "xxx", "ignore", "ignore"],
    },
  },
  fancyhdr: {
    macro: {
      "\\fancyhf": 1,
      "\\lhead": 1,
      "\\chead": 1,
      "\\rhead": 1,
      "\\lfoot": 1,
      "\\cfoot": 1,
      "\\rfoot": 1,
    },
  },
  geometry: {
    macro: {
      "\\geometry": 1,
      "\\newgeometry": 1,
      "\\restoregeometry": 0,
      "\\savegeometry": 1,
      "\\loadgeometry": 1,
    },
  },
  graphicx: {
    macro: {
      "\\DeclareGraphicsExtensions": 1,
      "\\graphicspath": 1,
      "\\includegraphics": ["[", "ignore", "ignore"],
      "\\includegraphics*": [
        "[",
        "ignore",
        "[",
        "ignore",
        "[",
        "ignore",
        "ignore",
      ],
      "\\rotatebox": 1,
      "\\scalebox": 1,
      "\\reflectbox": 1,
      "\\resizebox": 1,
    },
  },
  hyperref: {
    macro: {
      "\\hyperref": ["[", "ignore", "text"],
      "\\url": 1,
      "\\nolinkurl": 1,
      "\\href": ["ignore", "text"],
      "\\hyperlink": ["ignore", "text"],
      "\\hypertarget": ["ignore", "text"],
      "\\hyperbaseurl": 1,
      "\\hyperimage": ["ignore", "text"],
      "\\hyperdef": ["ignore", "ignore", "text"],
      "\\phantomsection": 0,
      "\\autoref": 1,
      "\\autopageref": 1,
      "\\hypersetup": 1,
      "\\urlstyle": 1,
      "\\pdfbookmark": 2,
      "\\currentpdfbookmark": 2,
      "\\subpdfbookmark": 2,
      "\\belowpdfbookmark": 2,
      "\\pdfstringref": 2,
      "\\texorpdfstring": ["text", "ignore"],
      "\\hypercalcbp": 1,
      "\\Acrobatmenu": 2,
    },
    macroCount: { "\\url": 1, "\\nolinkurl": 1 },
  },
  inputenc: { macro: { "\\inputencoding": 1 } },
  listings: {
    envir: { lstlisting: "xall" },
    macro: { "\\lstset": ["ignore"], "\\lstinputlisting": ["ignore"] },
  },
  psfig: { macro: { "\\psfig": 1 } },
  sectsty: {
    macro: {
      "\\allsectionsfont": 1,
      "\\partfont": 1,
      "\\chapterfont": 1,
      "\\sectionfont": 1,
      "\\subsectionfont": 1,
      "\\subsubsectionfont": 1,
      "\\paragraphfont": 1,
      "\\subparagraphfont": 1,
      "\\minisecfont": 1,
      "\\partnumberfont": 1,
      "\\parttitlefont": 1,
      "\\chapternumberfont": 1,
      "\\chaptertitlefont": 1,
      "\\nohang": 0,
    },
  },
  setspace: {
    envir: {
      singlespace: "text",
      "singlespace*": "text",
      onehalfspace: "text",
      doublespace: "text",
      spacing: "text",
    },
    // TeXcount 3.1.1 assigns `\url` to setspace a second time, replacing this list.
    macro: { "\\url": 1 },
  },
  url: {
    macro: {
      "\\url": 1,
      "\\urldef": 2,
      "\\urlstyle": 1,
      "\\DeclareUrlCommand": ["ignore", "xxx"],
    },
  },
  wrapfig: {
    envir: { wrapfigure: "float", wraptable: "float" },
    macro: { beginwrapfigure: 2, beginwraptable: 2 },
  },
  xparse: {},
};

/** Add one package's rules to a rule set. */
export function addPackage(rules: RuleSet, name: string): void {
  const pkg = PACKAGE_RULES[name];
  if (!pkg) {
    return;
  }
  for (const [key, rule] of Object.entries(pkg.macro ?? {})) {
    rules.macro.set(key, toRule(rule));
  }
  for (const [key, state] of Object.entries(pkg.envir ?? {})) {
    rules.envir.set(key, stateOf(state) ?? ST.ignore);
  }
  for (const [key, count] of Object.entries(pkg.macroCount ?? {})) {
    rules.macroCount.set(key, count);
  }
  if (name === "xparse") {
    addPackage(rules, "etoolbox");
  }
}
